import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
} from '@mui/material';
import { ClayChip } from '../ClayChip';

export function HeroSection() {
  const theme = useTheme();

  return (
    <Box sx={{ position: 'relative', pt: { xs: 14, md: 18 }, pb: { xs: 10, md: 16 } }}>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
            gap: { xs: 5, md: 7 },
            alignItems: 'center',
            px: { xs: 1, sm: 0 },
          }}
        >
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ClayChip
              label="AI-powered study loops"
              color="secondary"
              variant="filled"
              size="medium"
              sx={{
                mb: 3,
                borderRadius: 999,
                px: 2,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}
            />
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.05,
                mb: 3,
                fontSize: { xs: 36, sm: 44, md: 56 },
                color: 'common.white',
                textShadow: '0 18px 45px rgba(0,0,0,0.35)',
              }}
            >
              Master any subject with beautiful, adaptive study loops.
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                mb: 4,
                color: 'rgba(255,255,255,0.82)',
                maxWidth: '640px',
                lineHeight: 1.65,
                fontSize: { xs: 16.5, md: 18 },
              }}
            >
              Cycle through concepts until they stick. Our AI keeps you focused on what matters most,
              balancing new knowledge with targeted reviews so you remember longer.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                size="large"
                sx={{
                  px: 3.5,
                  py: 1.6,
                  fontWeight: 700,
                  fontSize: 16,
                  boxShadow: '0 20px 50px rgba(0,0,0,0.22)',
                }}
              >
                Start Your Learning Journey
              </Button>
              <Button
                component={Link}
                to="/features"
                variant="outlined"
                size="large"
                sx={{
                  px: 3.5,
                  py: 1.6,
                  fontWeight: 700,
                  fontSize: 16,
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.4)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                See how it works
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{ mt: 1.5, color: 'rgba(255,255,255,0.5)', display: 'block' }}
            >
              Free plan included · No credit card required · Pro from $9.99/mo
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              sx={{ mt: 5, color: 'rgba(255,255,255,0.7)' }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  20k+
                </Typography>
                <Typography variant="body2">Loops created by learners</Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  4.8/5
                </Typography>
                <Typography variant="body2">Avg. satisfaction score</Typography>
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }}>
                  2.5x
                </Typography>
                <Typography variant="body2">Faster retention vs. cramming</Typography>
              </Box>
            </Stack>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <Box
              sx={{
                position: 'relative',
                bgcolor: 'rgba(255,255,255,0.11)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: '20px',
                p: { xs: 3, md: 4 },
                overflow: 'hidden',
                boxShadow: [
                  '0 20px 50px rgba(0,0,0,0.45)',
                  '0 8px 16px rgba(0,0,0,0.28)',
                  'inset 0 2px 0 rgba(255,255,255,0.22)',
                  'inset 0 -2px 0 rgba(0,0,0,0.18)',
                ].join(', '),
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  width: 220,
                  height: 220,
                  background: `radial-gradient(circle, ${theme.palette.secondary.light} 0%, transparent 55%)`,
                  filter: 'blur(35px)',
                  opacity: 0.6,
                  top: -60,
                  right: -40,
                }}
              />
              <Typography
                variant="subtitle2"
                sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, letterSpacing: 1.1 }}
              >
                Adaptive Study Flow
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  mb: 2,
                  lineHeight: 1.4,
                }}
              >
                Plan, practice, and review in one elegant loop—automatically prioritized for you.
              </Typography>
              <Stack spacing={1.5} sx={{ color: 'rgba(255,255,255,0.82)' }}>
                {[
                  'Smart scheduling surfaces what needs attention next.',
                  'AI-crafted prompts keep you challenged but not overwhelmed.',
                  'Beautiful, minimal interface designed to stay out of your way.',
                ].map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        mt: 0.6,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        boxShadow: `0 0 0 6px rgba(255,255,255,0.06)`,
                      }}
                    />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}